// safety/incidents-and-injuries/pages/IncidentsInjuriesPage.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../../constants/permissions.constants';
import AccessRestricted from '../../../../components/AccessRestricted';
import { IncidentRegister } from '../components/IncidentRegister';
import type { RegisterFilters, RegisterSort } from '../components/IncidentRegister';
import { IncidentDetailPage } from '../components/IncidentDetailPage';
import { NewIncidentPage } from '../components/NewIncidentPage';
import { SafetySettings } from '../settings/SafetySettings';
import * as apiSvc from '../../../../utils/safety-incidents-api';
import { buildIncidentsCsv, downloadCsv } from '../utils/incidents-utils';
import type { IncidentAttachmentCategory } from '../../../../utils/safety-incidents-api';
import type { Attachment } from '../../../../types/index';
import type {
    Incident,
    IncidentStatus,
    SafetyLists,
    EmployeeRef,
    EntityRef,
    LetterTemplate,
} from '../../../../types/incident.types';

const DEFAULT_LISTS: SafetyLists = {
    causeOfInjury: ['Manual Handling', 'Slip / Trip / Fall', 'Struck By Object', 'Machinery', 'Vehicle', 'Chemical Exposure', 'Repetitive Strain'],
    bodyPart: ['Lower Back', 'Upper Back', 'Shoulder', 'Neck', 'Arm', 'Hand / Wrist', 'Leg', 'Knee', 'Ankle / Foot', 'Head', 'Eye'],
    natureOfInjury: ['Strain / Sprain', 'Fracture', 'Laceration', 'Bruise / Contusion', 'Burn', 'Crush', 'Concussion'],
    capacityStatus: ['Full Capacity', 'Light Duties', 'No Work', 'No Work – Full Restriction'],
    status: ['Open', 'In Progress', 'Under Investigation', 'Closed'],
};

const EMPTY_FILTERS: RegisterFilters = { capacity: '', entity: '', dateFrom: '', dateTo: '' };
const PAGE_SIZE = 20;

export const IncidentsInjuriesPage: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();

    const [view, setView] = useState<'list' | 'settings' | 'new' | 'detail'>('list');
    const [incidents, setIncidents] = useState<Incident[]>([]);
    const [lists, setLists] = useState<SafetyLists>(DEFAULT_LISTS);
    const [employees, setEmployees] = useState<EmployeeRef[]>([]);
    const [entities, setEntities] = useState<EntityRef[]>([]);
    const [templates, setTemplates] = useState<LetterTemplate[]>([]);

    // Server-side list state
    const [filter, setFilter] = useState<'All' | IncidentStatus>('All');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filters, setFilters] = useState<RegisterFilters>(EMPTY_FILTERS);
    const [sort, setSort] = useState<RegisterSort>({ by: 'createdAt', dir: 'desc' });
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({ open: 0, expiringCertificates: 0 });

    const [openId, setOpenId] = useState<number | null>(null);

    // Debounce the search box before it hits the server
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
        return () => clearTimeout(t);
    }, [search]);

    // Any filter/search change restarts at page 1
    useEffect(() => {
        setPage(1);
    }, [filter, debouncedSearch, filters]);

    const refreshStats = useCallback(() => {
        apiSvc.getIncidentStats().then(setStats).catch(() => {});
    }, []);

    const fetchIncidents = useCallback(async () => {
        setLoading(true);
        try {
            const { incidents: rows, total: count } = await apiSvc.listIncidents({
                status: filter === 'All' ? undefined : filter,
                search: debouncedSearch || undefined,
                capacity: filters.capacity || undefined,
                entity: filters.entity || undefined,
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
                sortBy: sort.by,
                sortDir: sort.dir,
                page,
                limit: PAGE_SIZE,
            });
            setIncidents(rows);
            setTotal(count);
        } catch {
            toast.error('Could not load incidents');
        } finally {
            setLoading(false);
        }
    }, [filter, debouncedSearch, filters, sort, page]);

    useEffect(() => {
        fetchIncidents();
    }, [fetchIncidents]);

    // One-time load: settings + reference data + alert stats
    useEffect(() => {
        (async () => {
            const [settings, emps, ents] = await Promise.all([
                apiSvc.getSettings().catch(() => null),
                apiSvc.getEmployees().catch(() => []),
                apiSvc.getEntities().catch(() => []),
            ]);
            if (settings) {
                setLists(settings.lists ?? DEFAULT_LISTS);
                setTemplates(settings.letterTemplates ?? []);
            }
            setEmployees(emps);
            setEntities(ents);
        })();
        refreshStats();
    }, [refreshStats]);

    const openIncident = incidents.find((i) => i.id === openId) ?? null;

    const updateIncidentInState = (updated: Incident) => {
        const old = incidents.find((i) => i.id === updated.id);
        setIncidents((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
        if (old && old.status !== updated.status) refreshStats();
    };

    const handleSort = (by: string) =>
        setSort((prev) => (prev.by === by ? { by, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { by, dir: 'asc' }));

    // Export every row matching the current filters (not just the visible page)
    const handleExport = async () => {
        try {
            const { incidents: rows } = await apiSvc.listIncidents({
                status: filter === 'All' ? undefined : filter,
                search: debouncedSearch || undefined,
                capacity: filters.capacity || undefined,
                entity: filters.entity || undefined,
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
                sortBy: sort.by,
                sortDir: sort.dir,
            });
            downloadCsv(`incident-register-${new Date().toISOString().slice(0, 10)}.csv`, buildIncidentsCsv(rows));
            toast.success(`Exported ${rows.length} records`);
        } catch {
            toast.error('Could not export incidents');
        }
    };

    const handleBulkStatus = async (ids: number[], status: IncidentStatus) => {
        try {
            const { updated, failed } = await apiSvc.bulkUpdateStatus(ids, status);
            if (failed.length === 0) {
                toast.success(`Updated ${updated} incident(s) to ${status}`);
            } else {
                toast.warning(`Updated ${updated} of ${ids.length} — ${failed.length} rejected by transition rules`);
            }
            fetchIncidents();
            refreshStats();
        } catch {
            toast.error('Could not update statuses');
        }
    };

    const persistTemplates = (next: LetterTemplate[]) => {
        setTemplates(next);
        apiSvc.saveSettings({ letterTemplates: next }).catch(() => toast.error('Could not save templates'));
    };

    const saveTemplate = (t: LetterTemplate) => {
        const exists = templates.some((x) => x.id === t.id);
        persistTemplates(exists ? templates.map((x) => (x.id === t.id ? t : x)) : [...templates, t]);
    };

    const toggleTemplateArchived = (id: string) => {
        persistTemplates(templates.map((t) => (t.id === id ? { ...t, archived: !t.archived } : t)));
    };

    if (!ability.can(ACTIONS.READ, RESOURCES.INCIDENT_INJURY)) {
        return <AccessRestricted />;
    }

    const canCreate = ability.can(ACTIONS.CREATE, RESOURCES.INCIDENT_INJURY);
    const canViewSettings = ability.can(ACTIONS.READ, RESOURCES.INCIDENT_INJURY_SETTINGS);

    if (view === 'new') {
        if (!canCreate) return <AccessRestricted />;
        return (
            <NewIncidentPage
                lists={lists}
                employees={employees}
                entities={entities}
                onCancel={() => setView('list')}
                onCreate={async (payload, attachments) => {
                    try {
                        const created = await apiSvc.createIncident(payload);

                        // Upload any files staged on the form, section by section
                        const staged: Array<[IncidentAttachmentCategory, Attachment[]]> = [
                            ['photos', attachments.photos],
                            ['incidentReports', attachments.incidentReports],
                            ['medicalDocuments', attachments.medicalDocuments],
                        ];
                        for (const [category, items] of staged) {
                            const files = items.map((a) => a.file).filter((f): f is File => !!f);
                            if (files.length === 0) continue;
                            try {
                                const { errors } = await apiSvc.uploadIncidentAttachments(created.id, category, files);
                                errors.forEach((e) => toast.error(`${e.filename}: upload failed`));
                            } catch {
                                toast.error(`Incident created, but ${files.length} file(s) could not be uploaded`);
                            }
                        }

                        toast.success(`Incident ${created.incidentNumber} created`);
                        setView('list');
                        setPage(1);
                        fetchIncidents();
                        refreshStats();
                    } catch {
                        toast.error('Could not create incident');
                    }
                }}
            />
        );
    }

    if (view === 'detail' && openIncident) {
        return (
            <IncidentDetailPage
                incident={openIncident}
                lists={lists}
                employees={employees}
                entities={entities}
                letterTemplates={templates}
                onBack={() => {
                    setView('list');
                    setOpenId(null);
                    fetchIncidents();
                }}
                onChange={updateIncidentInState}
                onSaveTemplate={saveTemplate}
                onArchiveTemplate={toggleTemplateArchived}
            />
        );
    }

    if (view === 'settings') {
        return (
            <SafetySettings
                lists={lists}
                employees={employees}
                entities={entities}
                templates={templates}
                onBack={() => setView('list')}
                onSave={(p) => {
                    if (p.lists) setLists(p.lists);
                    if (p.employees) setEmployees(p.employees);
                    if (p.entities) setEntities(p.entities);
                    if (p.templates) setTemplates(p.templates);
                }}
            />
        );
    }

    return (
        <IncidentRegister
            incidents={incidents}
            total={total}
            page={page}
            limit={PAGE_SIZE}
            loading={loading}
            filter={filter}
            search={search}
            filters={filters}
            sort={sort}
            stats={stats}
            lists={lists}
            entities={entities}
            onFilter={setFilter}
            onSearch={setSearch}
            onFiltersChange={setFilters}
            onSort={handleSort}
            onPage={setPage}
            onOpen={(id) => {
                setOpenId(id);
                setView('detail');
            }}
            onNew={canCreate ? () => setView('new') : undefined}
            onExport={handleExport}
            onBulkStatus={handleBulkStatus}
            onSettings={canViewSettings ? () => navigate('/safety/incidents/settings') : undefined}
        />
    );
};

export default IncidentsInjuriesPage;
